// Getting the app onto a new bundle (issue #3265).
//
// This used to live entirely inside useServiceWorkerHelper, which meant the only thing that could
// ask "is there a new version?" was a page navigation, and the only answer to "there is" was to
// reload the page that instant - including in the middle of a congregation singing, since the
// Player persists across pages. Both halves move here:
//
//   - checkForAppUpgrade() runs outside React, started by the music walk's view of the server's
//     bundle hash (noteServerBundleHash) - which the walk reports at launch and on every
//     live-update ping - with no navigation involved. Since issue #3910 that is the only trigger:
//     the page-navigation check is gone.
//   - applyAppUpdateWhenAllowed() routes the reload through the update gate, so a new bundle waits
//     for a break in the singing and for the user to stop working (an open dialog, an upload, a
//     planner cell mid-edit - issue #3265 PR D). That also makes the swap fast: Chrome will not
//     activate a new service worker while the old one has a request in flight, and the player's
//     streaming audio is such a request.
//
// The hook keeps what has to be a hook - useRegisterSW - and hands the registration and the
// updater down here.

import { localStorageGet, LocalStorageKey } from '../data/client_local_storage.ts';
import { getPlaybackSnapshot } from '../player/playback_state.ts';
import { canApplyUpdateNow, canReloadAppNow, isAppIdleForReload, runWhenUpdateAllowed } from '../player/update_gate.ts';

// A check asked in the first seconds after the app starts waits this long first. The browser runs
// its own check when the page loads, so an immediate one would only fetch the worker script twice.
// (This used to drop the check instead, against back-to-back refreshes that are now known to have
// been WebKit's false updates - issue #3910, see checkForAppUpgrade - and the first music walk
// usually reports the server's bundle inside this window, so a build found at launch was lost.)
const coolDownTimeMs = 5000;

export type ServiceWorkerUpdater = (reloadPage?: boolean) => Promise<void>;

let serviceWorkerUrl: string | undefined;
let serviceWorkerRegistration: ServiceWorkerRegistration | undefined;
let updateServiceWorker: ServiceWorkerUpdater | undefined;
let appStartTime = Date.now();
// the bundle hash the music walk last saw the server serving, for the reload log line
let latestServerBundleHash: string | undefined;

// Handed over by useServiceWorkerHelper from onRegisteredSW. Until it arrives, a check is a no-op.
export function setAppUpdateRegistration({
  swUrl,
  registration,
}: {
  swUrl: string;
  registration: ServiceWorkerRegistration | undefined;
}): void {
  serviceWorkerUrl = swUrl;
  serviceWorkerRegistration = registration;
}

// useRegisterSW's updateServiceWorker, which activates the waiting worker and reloads the page.
export function setAppUpdateServiceWorkerUpdater(updater: ServiceWorkerUpdater | undefined): void {
  updateServiceWorker = updater;
}

// Asks the browser whether a newer service worker exists. Setting `needRefresh`, and so the
// reload, is the browser's job from here: vite-plugin-pwa's registerType is 'prompt', so the
// update lands as a waiting worker and the hook calls applyAppUpdateWhenAllowed() below.
// Never throws - a failed check is a network problem and the next walk asks again.
//
// Only noteServerBundleHash() calls this, so the browser is asked only when the server is known to
// serve a different bundle (issue #3910). Until then every page navigation asked too, and on an
// iPhone that reloaded the app with nothing deployed: WebKit compares the certificate info of a
// worker script fetched for registration.update() with that of one fetched by its own check on
// page load, finds them different although the script is byte-for-byte the same, installs a "new"
// worker, we reload, and the check on the reload's page load disagrees with that worker in turn.
export async function checkForAppUpgrade(): Promise<void> {
  const swUrl = serviceWorkerUrl;
  const registration = serviceWorkerRegistration;
  if (!swUrl || !registration) {
    return;
  }
  const coolDownRemainingMs = coolDownTimeMs - (Date.now() - appStartTime);
  if (coolDownRemainingMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, coolDownRemainingMs));
  }

  if ('onLine' in navigator && !navigator.onLine) return;

  try {
    // bypass the offline cache to download the latest service worker
    const resp = await fetch(swUrl, {
      cache: 'no-store',
      headers: {
        cache: 'no-store',
        'cache-control': 'no-cache',
      },
    });

    // if successful, instruct Vite to check whether there was an update (will set `needRefresh` to true)
    if (resp?.status === 200) {
      await registration.update();
    }
  } catch (error: any) {
    console.log(`App update check failed: ${error?.message ?? error}`);
  }
}

// The music walk reports the bundle hash the server is serving (issue #3265), at launch, on every
// return to the foreground, when the app comes back online and on every live-update ping or
// reconnect. A difference from the hash this client is running means a new build is up, so check
// for the new worker now. `undefined` on either side means "no information": an older server that
// does not report the hash, or a client that never recorded one (a dev bundle, or a page whose
// scripts could not be read).
export async function noteServerBundleHash(bundleHash: string | undefined): Promise<void> {
  if (!bundleHash) {
    return;
  }
  latestServerBundleHash = bundleHash;
  const recordedBundleHash = localStorageGet(LocalStorageKey.BundleHash);
  if (!recordedBundleHash || recordedBundleHash === bundleHash) {
    return;
  }
  if (serviceWorkerRegistration?.installing || serviceWorkerRegistration?.waiting) {
    // the new worker is on its way, or installed and waiting on the playback gate (which can hold a
    // reload for a long time, with every walk meanwhile reporting the same new hash): another check
    // would only fetch the worker script again. Any other walk that still sees a different bundle
    // asks again, which is how a check that failed on the network gets its retry.
    return;
  }
  console.log(`Server is serving bundle ${bundleHash}, this client is running ${recordedBundleHash}`);
  await checkForAppUpgrade();
}

// The gate key the page reload is held under; distinct from song_reload.ts's 'song-files', so a
// held-back bundle reload and a held-back song refresh do not evict each other.
const appReloadUpdateKey = 'app-reload';

// A new worker is waiting. Reload onto it at the next moment that does not interrupt singing or
// take away work in progress - which is immediately, unless a song is playing or was only just
// paused, or the user is in the middle of something (issue #3265).
export function applyAppUpdateWhenAllowed(): void {
  // both hashes, so the log shows whether a reload was for a new build (issue #3910)
  const bundles = `running bundle ${localStorageGet(LocalStorageKey.BundleHash)}, server bundle ${latestServerBundleHash ?? 'not reported yet'}`;
  console.log(`*** New service worker detected (${bundles}), ${describeAppReloadTiming(Date.now())} ***`);
  runWhenUpdateAllowed(
    () => {
      void updateServiceWorker?.(/*reloadPage*/ true);
    },
    // exclusive: the page is going away, so nothing else pending (a song reload, say) is worth starting
    // waitForUserIdle: a reload loses an uncommitted planner cell or an upload in flight
    { key: appReloadUpdateKey, exclusive: true, waitForUserIdle: true },
  );
}

// Says which half of the gate is holding the reload, because the two waits feel very different from
// the log's point of view: seconds for a user who has stopped typing, up to ten minutes for a
// player that was just paused. Also used by the reload for a dead IndexedDB connection
// (storage_reload.ts), which goes through the same gate.
export function describeAppReloadTiming(now: number): string {
  if (canReloadAppNow(now)) {
    return 'reloading page';
  }
  const waitsForPlayback = !canApplyUpdateNow(getPlaybackSnapshot(), now);
  if (waitsForPlayback && !isAppIdleForReload(now)) {
    return 'reloading page once playback stops and the app is idle';
  }
  return waitsForPlayback ? 'reloading page once playback stops' : 'reloading page once the app is idle';
}

// test-only: the registration and the start time are module state for the life of the app
export function resetAppUpdateForTests(): void {
  serviceWorkerUrl = undefined;
  serviceWorkerRegistration = undefined;
  updateServiceWorker = undefined;
  appStartTime = Date.now();
  latestServerBundleHash = undefined;
}
